import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { toast } from 'react-toastify';

import PeopleIcon from '@material-ui/icons/People';
import SaveAlt from '@material-ui/icons/SaveAlt';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';

import Paper from '~/components/Paper';
import Button from '~/components/Button';
import Table from '~/components/Table/Table';
import GridItem from '~/components/GridItem';
import InputDate from '~/components/Input/InputDate';
import GridContainer from '~/components/GridContainer';
import InputSearch from '~/components/Input/InputSearch';
import LinearProgress from '~/components/LinearProgress';
import tableStyles from '~/components/Table/Table/styles';
import InputControl from '~/components/Input/InputControl';
import TablePagination from '~/components/Table/TablePagination';
import Request from '~/providers/Request';

import CurrentUser from '~/contexts/CurrentUser';

import toInt from '~/utils/Number/toInt';
import isPresent from '~/utils/isPresent';
import csvGenerator from '~/utils/csvGenerator';
import xlsxGenerator from '~/utils/xlsxGenerator';

function ReportsEpay() {
  const { t } = useTranslation();
  const classesTable = tableStyles();
  const { user } = useContext(CurrentUser);

  const [loading, setLoading] = useState(false);
  const [epayReports, setEpayReports] = useState([]);
  const [registers, setRegisters] = useState(null);
  const [totalEpayReports, setTotalEpayReports] = useState(0);
  const [dataInit, setDataInit] = useState(moment().startOf('month').format());
  const [dataEnd, setDataEnd] = useState(moment().endOf('month').format());

  const [inputSearch, setInputSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const trAttr = (attr) => t(`reportsEpay.attributes.${attr}`);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  async function generateFile(filters, type) {
    const corporation_name = 'Epay';
    try {
      const rows = [];

      rows.push([t('dashboard.attributes.users_by_month').toUpperCase()]);
      rows.push(['']);

      rows.push(['Empresa', corporation_name]);
      rows.push(['Data Inicial do Relatório', `${moment(dataInit).format('DD/MM/YYYY HH:mm')}`]);
      rows.push(['Data Final do Relatório', `${moment(dataEnd).format('DD/MM/YYYY HH:mm')}`]);
      rows.push(['']);

      rows.push([
        trAttr('pin_activated_date'),
        trAttr('person_name'),
        trAttr('cpf_cnpj'),
        trAttr('value'),
        trAttr('payment_way'),
        trAttr('pin'),
        trAttr('status_nfe'),
      ]);

      epayReports.map((item) => {
        rows.push([
          moment(item.activated_at).format('L'),
          item.user.name,
          item.user.cpf,
          formatAmount(item.product.amount),
          'N/A',
          item.code,
          'N/A',
        ]);

        return item;
      });

      rows.push(['']);

      rows.push(['Total de PIN', epayReports.length]);
      rows.push(['Valor total de venda(R$)', someTotalAmount()]);

      rows.push(['']);

      rows.push(['Relatório gerado por', user.name]);
      rows.push(['Relatório gerado em', `${moment().format('L')} - ${moment().format('LT')}`]);

      if (type === 'csv') {
        csvGenerator(`televida-${corporation_name}.csv`, [...rows]);
      } else {
        xlsxGenerator(`televida-${corporation_name}.xlsx`, [...rows]);
      }

      toast.success(
        `Arquivo ${type.toLocaleUpperCase()} gerado com sucesso!`
      );

      const totalPages =
        toInt(registers.meta.total) / registers.meta.perPage;
      if (registers.meta.page < totalPages) {
        filters.page += 1;
        filters.corporation_name = corporation_name;
        generateFile(filters, type);
      }
    } catch (error) {
      console.log(error);
      toast.error(
        `Não foi possível gerar o arquivo ${type.toLocaleUpperCase()}`
      );
    }
  }

  function onExport(type) {
    const filters = {
      qs: '',
      page: 1,
      per_page: 50000,
    };

    generateFile(filters, type);
  }

  async function getEpayReports() {
    setLoading(true);

    const filters = {
      start: dataInit,
      end: dataEnd,
      qs: inputSearch,
      page: page + 1,
      per_page: rowsPerPage,
    };

    Request.get('/admin/report/epay', filters, { normalizeJSON: false })
      .then((response) => {
        setLoading(false);
        setRegisters(response);
        setEpayReports(response.data);
        setPage(toInt(response.meta.page) - 1);
        setTotalEpayReports(toInt(response.meta.total));
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  }

  async function fetchParams() {
    const url = new URLSearchParams(window.location.search);
    const start = url.get('start');
    const end = url.get('end');

    if (isPresent(start)) {
      const startDate = moment(start);

      if (startDate.isValid()) {
        setDataInit(startDate.format());
      }
    }

    if (isPresent(end)) {
      const endDate = moment(end);

      if (endDate.isValid()) {
        setDataEnd(endDate.format());
      }
    }
  }

  useEffect(() => {
    if (dataInit && dataEnd) {
      getEpayReports();
    }
  }, [
    inputSearch,
    page,
    rowsPerPage,
    dataInit,
    dataEnd,
  ]);

  useEffect(() => {
    fetchParams();
  }, []);

  const formatAmount = (value) => {
    const valueString = value.toString();
    const position = valueString.length - 2;
    const output = [valueString.slice(0, position), '.', valueString.slice(position)].join('');
    return Number(output).toLocaleString('pt-BR', { minimumFractionDigits: 2, style: 'currency', currency: 'BRL' });
  };

  const someTotalAmount = () => {
    let some = 0;
    epayReports.map((item) => {
      some += item.product.amount;
    });

    return formatAmount(some);
  };

  return (
    <>
      <Paper
        Icon={PeopleIcon}
        iconColor="blue"
        title={trAttr('epay_reports')}
        subtitle={trAttr('epay_reports_description')}
      >
        <GridContainer>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputControl
              value={dataInit}
              name="date"
              label={t('reportsEpay.extras.date_init')}
              inputComponent={InputDate}
              InputProps={{
                type: 'date',
              }}
              onChange={(date) => {
                setDataInit(moment(date).startOf('day').format());
              }}
            />
          </GridItem>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputControl
              value={dataEnd}
              name="date"
              label={t('reportsEpay.extras.date_end')}
              inputComponent={InputDate}
              InputProps={{
                type: 'date',
              }}
              onChange={(date) =>
                setDataEnd(moment(date).endOf('day').format())
              }
            />
          </GridItem>
          <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
            <InputSearch
              value={inputSearch}
              label={t('action.search')}
              placeholder={t('reportsEpay.extras.search')}
              onChange={setInputSearch}
            />
          </GridItem>
          <GridItem align="right">
            <Button
              color="second"
              onClick={() => {
                onExport('csv');
              }}
            >
              {t('action.export_csv')} <SaveAlt />
            </Button>
            <Button
              style={{ marginLeft: 10 }}
              color="orange"
              onClick={() => {
                onExport('xlsx');
              }}
            >
              {t('action.export_xlsx')} <SaveAlt />
            </Button>
          </GridItem>
        </GridContainer>

        <LinearProgress loading={loading} />

        <Table
          emptyData={epayReports.length === 0}
          loading={loading}
          columns={
            <>
              <TableCell className={classesTable.cell}>
                {trAttr('pin_activated_date')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('person_name')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('cpf_cnpj')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('value')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('payment_way')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('pin')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('status_nfe')}
              </TableCell>
            </>
          }
        >
          {epayReports.length > 0 && epayReports.map((row) => {
            return (
              <TableRow key={`${row.id}-${row.corporation_name}`} hover className={classesTable.row}>
                <TableCell>{moment(row.activated_at).format('L')}</TableCell>
                <TableCell>{row.user.name}</TableCell>
                <TableCell>{row.user.cpf}</TableCell>
                <TableCell>{formatAmount(row.product.amount)}</TableCell>
                <TableCell>N/A</TableCell>
                <TableCell>{row.code}</TableCell>
                <TableCell>N/A</TableCell>
              </TableRow>
            );
          })}
        </Table>

        <LinearProgress loading={loading} />

        <TablePagination
          count={totalEpayReports}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </>
  );
}

export default ReportsEpay;
